import { createBrowserRouter, Navigate } from "react-router";
import RequireCapability from "./auth/RequireCapability";
import RequireProviderAdmin from "./auth/RequireProviderAdmin";
import ErrorPage from "./components/layout/ErrorPage";
import RootLayout from "./components/layout/RootLayout";
import AccessTreePage from "./pages/access-tree/AccessTree";
import ActivityLog from "./pages/activity/ActivityLog";
import AuthorizationModelDetail from "./pages/authorization/AuthorizationModelDetail";
import AuthorizationModels from "./pages/authorization/AuthorizationModels";
import RelationshipCheck from "./pages/authorization/RelationshipCheck";
import StoreTests from "./pages/authorization/StoreTests";
import TupleBrowser from "./pages/authorization/TupleBrowser/TupleBrowser";
import BlueprintBuilder from "./pages/blueprints/BlueprintBuilder";
import BlueprintSlugRedirect from "./pages/blueprints/BlueprintSlugRedirect";
import BlueprintViewer from "./pages/blueprints/BlueprintViewer";
import ResourceCatalog from "./pages/blueprints/ResourceCatalog";
import ClientDetail from "./pages/clients/ClientDetail/ClientDetail";
import Clients from "./pages/clients/Clients";
import NewStandaloneClient from "./pages/clients/NewStandaloneClient";
import CompaniesPage from "./pages/companies/CompaniesPage";
import Dashboard from "./pages/dashboard/Dashboard";
import {
  DOC_SECTIONS,
  FIRST_DOC_SECTION,
  FIRST_DOC_SLUG,
} from "./pages/docs/content";
import Documentation from "./pages/docs/Documentation";
import Grants from "./pages/grants/Grants";
import ManagementAccessTree from "./pages/management/ManagementAccessTree";
import ManagementActivityLog from "./pages/management/ManagementActivityLog";
import ManagementAuthorizationModelDetail from "./pages/management/ManagementAuthorizationModelDetail";
import ManagementAuthorizationModels from "./pages/management/ManagementAuthorizationModels";
import ManagementStore from "./pages/management/ManagementStore";
import MyHub from "./pages/myhub/MyHub";
import PlatformDetail from "./pages/platforms/PlatformDetail/PlatformDetail";
import Platforms from "./pages/platforms/Platforms";
import RegisterPlatform from "./pages/platforms/RegisterPlatform/RegisterPlatform";
import Requests from "./pages/requests/Requests";
import SpiffeClusters from "./pages/spiffe/SpiffeClusters";

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    ErrorBoundary: ErrorPage,
    children: [
      { index: true, Component: Dashboard },
      // The dashboard used to live on /home — keep old bookmarks working.
      { path: "home", element: <Navigate to="/" replace /> },

      // Clients — all clients (the full superset)
      { path: "clients", Component: Clients },
      { path: "clients/new", Component: NewStandaloneClient },
      { path: "clients/:id", Component: ClientDetail },

      // Platforms — a collection of clients plus one blueprint (ADR-0056)
      { path: "platforms", Component: Platforms },
      { path: "platforms/new", Component: RegisterPlatform },
      { path: "platforms/:id", Component: PlatformDetail },

      // MyHub — open to every authenticated user.
      { path: "myhub", Component: MyHub },

      // Requests — API access and data access requests
      { path: "requests", Component: Requests },

      // SPIFFE clusters — consumer cluster registration and approval. Owner-gated.
      {
        path: "spiffe-clusters",
        element: (
          <RequireCapability capability="spiffe.manage">
            <SpiffeClusters />
          </RequireCapability>
        ),
      },

      // Documentation — consumer docs. Open to every authenticated user; no
      // capability gate. Each section has a redirect to its first page.
      {
        path: "docs",
        element: (
          <Navigate
            to={`/docs/${FIRST_DOC_SECTION}/${FIRST_DOC_SLUG}`}
            replace
          />
        ),
      },
      ...DOC_SECTIONS.map((sec) => ({
        path: `docs/${sec.key}`,
        element: (
          <Navigate to={`/docs/${sec.key}/${sec.entries[0].slug}`} replace />
        ),
      })),
      { path: "docs/:section/:slug", Component: Documentation },

      // Blueprints — platform resource hierarchies. Accessible to all users;
      // write actions are gated per-platform (blueprint:<slug> maintainer).
      {
        path: "blueprints",
        element: <Navigate to="/blueprints/builder" replace />,
      },
      { path: "blueprints/builder", Component: BlueprintBuilder },
      { path: "blueprints/builder/:platform", Component: BlueprintBuilder },
      // The blueprint list page is removed. Old bookmarks go to Platforms,
      // not to the slug redirect below.
      {
        path: "blueprints/overview",
        element: <Navigate to="/platforms" replace />,
      },
      // A blueprint lives in the Blueprint tab of its platform page. Old
      // links and bookmarks redirect there.
      { path: "blueprints/:slug", Component: BlueprintSlugRedirect },
      { path: "blueprints/viewer", Component: BlueprintViewer },
      // The resource catalog moved out of the removed Blueprints nav group.
      {
        path: "blueprints/resource-catalog",
        element: <Navigate to="/blueprint-resources" replace />,
      },
      { path: "blueprint-resources", Component: ResourceCatalog },

      // Companies: customer onboarding (ADR-0058). keycloak-api needs admin on
      // provider:intility for every route of it (decision 8), so the page does too.
      {
        path: "companies",
        element: (
          <RequireProviderAdmin>
            <CompaniesPage />
          </RequireProviderAdmin>
        ),
      },

      // Audit Log — self-scoping (ADR-0033): the API returns only events within the caller's
      // own authority scope, so authentication is the only gate here. The management audit log
      // below stays behind governance.read with the rest of the management section.
      { path: "activity", element: <Navigate to="/audit-log" replace /> },
      { path: "audit-log", element: <ActivityLog /> },

      // Authorization — OpenFGA on the fixed access store.
      // Gated on store:access viewer (authorization.read) in the management store.
      {
        path: "authorization",
        element: <Navigate to="/authorization/model" replace />,
      },
      ...[
        { path: "authorization/model", Page: AuthorizationModels },
        {
          path: "authorization/model/:modelId",
          Page: AuthorizationModelDetail,
        },
        { path: "authorization/tuples", Page: TupleBrowser },
        { path: "authorization/check", Page: RelationshipCheck },
        { path: "authorization/tests", Page: StoreTests },
      ].map(({ path, Page }) => ({
        path,
        element: (
          <RequireCapability capability="authorization.read">
            <Page />
          </RequireCapability>
        ),
      })),

      // Access Tree — read-only effective-access inspection. Self-scoping (ADR-0033):
      // fga-api returns only what the caller can actually see (own covering roots free,
      // expansions need reader on the prefix, another subject needs the top relation),
      // so authentication is the only gate here — same pattern as /audit-log above.
      { path: "access-tree", element: <AccessTreePage /> },

      // Grants — bulk grant/revoke on the access store (ADR-0047). Self-scoping
      // (ADR-0033): the object suggestions come from fga-api for what the caller
      // holds admin on, and the server enforces every write, so authentication
      // is the only gate here — same pattern as /access-tree above.
      { path: "grants", element: <Grants /> },

      // Management Store — access-over-access for Keycloak/OpenFGA (separate store).
      // Gated on store:management viewer (governance.read).
      ...[
        { path: "management", Page: ManagementStore },
        { path: "management/model", Page: ManagementAuthorizationModels },
        {
          path: "management/model/:modelId",
          Page: ManagementAuthorizationModelDetail,
        },
        { path: "management/access-tree", Page: ManagementAccessTree },
        { path: "management/audit-log", Page: ManagementActivityLog },
      ].map(({ path, Page }) => ({
        path,
        element: (
          <RequireCapability capability="governance.read">
            <Page />
          </RequireCapability>
        ),
      })),
    ],
  },
]);
